/**
 * useDragDropTexture Hook
 * EXACT PORT dari ModelViewerBACKUP.jsx line 4652-7472
 * 
 * Drag & Drop texture system:
 * - Detect mesh under cursor (raycasting)
 * - Apply image/video textures
 * - Visual feedback (hover, drag cursor)
 * - Material name filtering
 * - Video texture support dengan sync
 * 
 * @param {Object} refs - Container, camera, scene, model, raycaster, mouse refs
 * @param {Object} videoElementsRef - Video elements reference
 * @param {Object} videoSyncMode - Current video sync mode
 * @param {Function} updateTexturesList - Callback to update textures
 * @returns {Object} Drag & drop state dan handlers
 */

import { useState, useCallback, useRef } from 'react'

export function useDragDropTexture(refs, videoElementsRef, videoSyncMode, updateTexturesList) {
  const {
    containerRef,
    cameraRef,
    sceneRef,
    modelRef,
    raycasterRef,
    mouseRef,
    selectedCameraRef,
  } = refs

  const [isDragging, setIsDragging] = useState(false)
  const [hoveredMesh, setHoveredMesh] = useState(null)
  const previousHoveredMeshRef = useRef(null)

  // Helper: Check if material name is allowed for texture editing
  const isAllowedMaterialName = useCallback((materialName) => {
    const allowedMaterialNames = [
      'Edit Here',
      'Screen',
      'Body Color',
      'Background',
    ]

    // Check exact match first
    if (allowedMaterialNames.includes(materialName)) {
      return true
    }

    // Check if material name starts with allowed name followed by .XXX (numeric suffix)
    return allowedMaterialNames.some((baseName) => {
      if (materialName.startsWith(baseName + '.')) {
        const suffix = materialName.substring(baseName.length + 1)
        return /^\d+$/.test(suffix)
      }
      return false
    })
  }, [])

  // Helper: Get active camera (default or GLB)
  const getActiveCamera = useCallback(() => {
    let activeCamera = cameraRef.current

    if (
      selectedCameraRef?.current !== 'default' &&
      modelRef.current?.userData?.cameras
    ) {
      const cameraData = modelRef.current.userData.cameras.find(
        (c) => c.name === selectedCameraRef.current
      )
      if (cameraData && cameraData.camera) {
        activeCamera = cameraData.camera
        activeCamera.updateMatrixWorld(true)
      }
    }

    return activeCamera
  }, [cameraRef, modelRef, selectedCameraRef])

  // Detect mesh under cursor dengan raycasting
  const detectMeshUnderCursor = useCallback(
    (event) => {
      if (
        !containerRef.current ||
        !cameraRef.current ||
        !sceneRef.current ||
        !raycasterRef.current ||
        !mouseRef.current ||
        !modelRef.current
      ) {
        return null
      }

      const rect = containerRef.current.getBoundingClientRect()
      mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
      mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1

      // Use active camera untuk accurate raycasting
      const activeCamera = getActiveCamera()
      raycasterRef.current.setFromCamera(mouseRef.current, activeCamera)

      // Get meshes dengan allowed material names
      const meshes = []
      modelRef.current.traverse((child) => {
        if (child.isMesh && child.material) {
          const material = Array.isArray(child.material)
            ? child.material[0]
            : child.material

          const materialName = material.name || ''
          if (isAllowedMaterialName(materialName)) {
            meshes.push(child)
          }
        }
      })

      const intersects = raycasterRef.current.intersectObjects(meshes, false)

      if (intersects.length > 0) {
        return intersects[0].object
      }

      return null
    },
    [containerRef, cameraRef, sceneRef, raycasterRef, mouseRef, modelRef, getActiveCamera, isAllowedMaterialName]
  )

  // Apply texture to mesh (support image & video)
  const applyTextureToMesh = useCallback(
    (mesh, file) => {
      if (!file || !window.THREE_MODULE) return

      const THREE = window.THREE_MODULE
      const fileExtension = file.name.split('.').pop().toLowerCase()
      const isVideo = ['mp4', 'webm', 'ogg', 'mov'].includes(fileExtension)

      if (isVideo) {
        // Handle video texture - synced with animation timeline
        const videoUrl = URL.createObjectURL(file)
        const video = document.createElement('video')
        video.src = videoUrl
        video.crossOrigin = 'anonymous'
        video.loop = false // Controlled by animation timeline
        video.muted = true
        video.playsInline = true
        video.preload = 'auto'
        video.load()

        video.addEventListener('loadedmetadata', () => {
          setTimeout(() => {
            let duration = video.duration

            if (!isFinite(duration) || duration <= 0) {
              console.error('Invalid video duration:', duration)
              alert('Video file is invalid or corrupted. Please use a different video.')
              return
            }

            const videoTexture = new THREE.VideoTexture(video)

            // Configure texture
            videoTexture.wrapS = THREE.RepeatWrapping
            videoTexture.wrapT = THREE.RepeatWrapping
            videoTexture.repeat.set(-1, 1) // Flip X
            videoTexture.rotation = Math.PI // Rotate 180°
            videoTexture.center.set(0.5, 0.5)

            // Set color space
            if (videoTexture.colorSpace !== undefined) {
              videoTexture.colorSpace = THREE.SRGBColorSpace
            } else {
              videoTexture.encoding = THREE.sRGBEncoding
            }

            videoTexture.needsUpdate = true

            // Get materials
            const materials = Array.isArray(mesh.material)
              ? mesh.material
              : [mesh.material]

            materials.forEach((material) => {
              if (material) {
                // Copy properties from old texture if exists
                const oldTexture = material.map
                if (oldTexture) {
                  videoTexture.wrapS = oldTexture.wrapS
                  videoTexture.wrapT = oldTexture.wrapT
                  videoTexture.repeat.copy(oldTexture.repeat)
                  videoTexture.offset.copy(oldTexture.offset)
                  videoTexture.rotation = oldTexture.rotation
                }

                // Apply video texture
                material.map = videoTexture
                material.needsUpdate = true
              }
            })

            // Store video element dengan metadata
            videoElementsRef.current[videoTexture.uuid] = {
              video: video,
              videoDuration: duration,
              textureId: videoTexture.uuid,
              isSynced: true,
              syncMode: videoSyncMode,
              isBuffering: false,
              lastSeekTime: 0,
            }

            console.log(
              `Video texture applied: ${mesh.name || 'Unnamed'}, duration: ${duration.toFixed(2)}s`
            )

            // Pre-buffer video
            console.log('🎬 Pre-buffering video...')
            video.currentTime = 0
            video
              .play()
              .then(() => {
                setTimeout(
                  () => {
                    video.pause()
                    video.currentTime = 0
                    console.log('✓ Video buffered')
                  },
                  Math.min(duration * 1000, 2000)
                )
              })
              .catch((err) => {
                console.warn('Pre-buffer failed:', err)
              })

            // Update textures list
            if (updateTexturesList) updateTexturesList()
          }, 100)
        })

        video.addEventListener('error', (error) => {
          console.error('Error loading video:', error)
          alert('Failed to load video texture.')
        })
      } else {
        // Handle image texture
        const reader = new FileReader()

        reader.onload = (e) => {
          const textureLoader = new THREE.TextureLoader()
          textureLoader.load(
            e.target.result,
            (newTexture) => {
              // Configure texture
              newTexture.wrapS = THREE.RepeatWrapping
              newTexture.wrapT = THREE.RepeatWrapping
              newTexture.repeat.set(-1, 1) // Flip X
              newTexture.rotation = Math.PI // Rotate 180°
              newTexture.center.set(0.5, 0.5)

              // Set color space
              if (newTexture.colorSpace !== undefined) {
                newTexture.colorSpace = THREE.SRGBColorSpace
              } else {
                newTexture.encoding = THREE.sRGBEncoding
              }

              newTexture.needsUpdate = true

              // Get materials
              const materials = Array.isArray(mesh.material)
                ? mesh.material
                : [mesh.material]

              materials.forEach((material) => {
                if (material) {
                  // Copy properties from old texture if exists
                  const oldTexture = material.map
                  if (oldTexture) {
                    newTexture.wrapS = oldTexture.wrapS
                    newTexture.wrapT = oldTexture.wrapT
                    newTexture.repeat.copy(oldTexture.repeat)
                    newTexture.offset.copy(oldTexture.offset)
                    newTexture.rotation = oldTexture.rotation
                  }

                  // Apply texture
                  material.map = newTexture
                  material.needsUpdate = true
                }
              })

              console.log(`Texture applied: ${mesh.name || 'Unnamed'}`)

              // Update textures list
              if (updateTexturesList) updateTexturesList()
            },
            undefined,
            (error) => {
              console.error('Error loading texture:', error)
              alert('Failed to load texture.')
            }
          )
        }

        reader.readAsDataURL(file)
      }
    },
    [videoElementsRef, videoSyncMode, updateTexturesList]
  )

  // Handle drag over
  const handleDragOver = useCallback(
    (event) => {
      event.preventDefault()
      event.stopPropagation()

      const mesh = detectMeshUnderCursor(event)

      // Only update if mesh changed (prevent flickering)
      if (mesh !== hoveredMesh) {
        if (mesh && hoveredMesh && mesh.uuid === hoveredMesh.uuid) {
          return // Same mesh
        }
        setHoveredMesh(mesh)
      }
    },
    [detectMeshUnderCursor, hoveredMesh]
  )

  // Handle drag enter
  const handleDragEnter = useCallback((event) => {
    event.preventDefault()
    event.stopPropagation()

    if (event.dataTransfer.types.includes('Files')) {
      setIsDragging(true)
    }
  }, [])

  // Handle drag leave
  const handleDragLeave = useCallback(
    (event) => {
      event.preventDefault()
      event.stopPropagation()

      if (event.target === containerRef.current) {
        setIsDragging(false)
        setHoveredMesh(null)
      }
    },
    [containerRef]
  )

  // Handle drop
  const handleDrop = useCallback(
    (event) => {
      event.preventDefault()
      event.stopPropagation()

      setIsDragging(false)

      const files = event.dataTransfer.files
      if (files.length === 0) {
        setHoveredMesh(null)
        return
      }

      const file = files[0]

      // Check file type
      if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
        alert('Please drop an image or video file (PNG, JPG, MP4, WebM, etc.)')
        setHoveredMesh(null)
        return
      }

      // Get hovered mesh
      const targetMesh = hoveredMesh

      if (!targetMesh || !window.THREE_MODULE) {
        alert('Please drag the texture over a 3D object')
        setHoveredMesh(null)
        return
      }

      // Apply texture
      applyTextureToMesh(targetMesh, file)
      setHoveredMesh(null)
    },
    [hoveredMesh, applyTextureToMesh]
  )

  return {
    // State
    isDragging,
    hoveredMesh,
    setHoveredMesh,
    previousHoveredMeshRef,

    // Handlers
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop,

    // Helpers
    detectMeshUnderCursor,
    applyTextureToMesh,
  }
}

